import { useState, useMemo, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  rfwPolls,
  getAllPolls,
  getPollsByType,
  getPollsByLocation,
  getPollsByStatus,
  getActivePolls,
  getPopularPolls,
  getCabinaPolls,
  getPollById,
  formatCount,
  formatPollDate,
  formatPollDateShort,
  getTimeRemainingText,
  getWinnerText,
  pollTypeLabels,
  pollTypeIcons,
  pollTypeColors,
  pollTypeBgLight,
  pollTypeTextColors,
  pollTypeDotColors,
  pollLocationLabels,
  pollLocationIcons,
  pollLocationColors,
  pollDashboardStats,
  type Poll,
  type PollType,
  type PollLocation,
  type PollStatus,
} from '@/mocks/rfwPolls';

const VOTED_KEY_PREFIX = 'rfw_poll_voted_';

function loadVotedSet(): Record<string, string[]> {
  try {
    const raw = localStorage.getItem('rfw_polls_voted_all');
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveVotedSet(data: Record<string, string[]>) {
  localStorage.setItem('rfw_polls_voted_all', JSON.stringify(data));
}

type PageState = 'loading' | 'error' | 'ready';

export default function EncuestasPage() {
  const [pageState, setPageState] = useState<PageState>('loading');
  const [typeFilter, setTypeFilter] = useState<PollType | 'todos'>('todos');
  const [locationFilter, setLocationFilter] = useState<PollLocation | 'todos'>('todos');
  const [statusFilter, setStatusFilter] = useState<PollStatus | 'todos'>('todos');
  const [selectedPoll, setSelectedPoll] = useState<Poll | null>(null);
  const [votedData, setVotedData] = useState<Record<string, string[]>>(() => loadVotedSet());
  const [detailTab, setDetailTab] = useState<'votar' | 'resultados' | 'score'>('votar');

  useEffect(() => {
    const t = setTimeout(() => setPageState('ready'), 500);
    return () => clearTimeout(t);
  }, []);

  // Vote handler
  const handleVote = useCallback((pollId: string, optionId: string, pollType: PollType) => {
    setVotedData((prev) => {
      const existing = prev[pollId] || [];
      let nextOptions: string[];
      if (pollType === 'single' || pollType === 'quick') {
        nextOptions = existing.includes(optionId) ? [] : [optionId];
      } else if (pollType === 'multiple') {
        nextOptions = existing.includes(optionId)
          ? existing.filter((id) => id !== optionId)
          : [...existing, optionId];
      } else {
        nextOptions = existing.includes(optionId)
          ? existing.filter((id) => id !== optionId)
          : [...existing, optionId];
      }
      const next = { ...prev, [pollId]: nextOptions };
      if (nextOptions.length === 0) delete next[pollId];
      saveVotedSet(next);
      return next;
    });
  }, []);

  const getVotedOptions = useCallback((pollId: string): string[] => {
    return votedData[pollId] || [];
  }, [votedData]);

  // Filtering
  const filtered = useMemo(() => {
    let result = getAllPolls();
    if (typeFilter !== 'todos') result = result.filter((p) => p.type === typeFilter);
    if (locationFilter !== 'todos') result = result.filter((p) => p.location === locationFilter);
    if (statusFilter !== 'todos') result = result.filter((p) => p.status === statusFilter);
    result.sort((a, b) => {
      const order: Record<string, number> = { active: 0, scheduled: 1, closed: 2 };
      if (order[a.status] !== order[b.status]) return order[a.status] - order[b.status];
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
    return result;
  }, [typeFilter, locationFilter, statusFilter]);

  const cabinaActive = useMemo(() => getCabinaPolls(), []);

  const handleRetry = useCallback(() => {
    setPageState('loading');
    setTimeout(() => setPageState('ready'), 500);
  }, []);

  // Type tabs
  const typeTabs: { id: PollType | 'todos'; label: string; icon: string }[] = [
    { id: 'todos', label: 'Todos', icon: 'ri-grid-line' },
    { id: 'single', label: 'Una Respuesta', icon: pollTypeIcons.single },
    { id: 'multiple', label: 'Múltiples', icon: pollTypeIcons.multiple },
    { id: 'quick', label: 'Rápida', icon: pollTypeIcons.quick },
    { id: 'ranking', label: 'Ranking', icon: pollTypeIcons.ranking },
    { id: 'elimination', label: 'Eliminación', icon: pollTypeIcons.elimination },
  ];

  // Location tabs
  const locationTabs: { id: PollLocation | 'todos'; label: string; icon: string }[] = [
    { id: 'todos', label: 'Todos', icon: 'ri-grid-line' },
    { id: 'cabina', label: 'Cabina', icon: pollLocationIcons.cabina },
    { id: 'radio', label: 'Radio', icon: pollLocationIcons.radio },
    { id: 'programa', label: 'Programa', icon: pollLocationIcons.programa },
    { id: 'locutor', label: 'Locutor', icon: pollLocationIcons.locutor },
    { id: 'club', label: 'Club', icon: pollLocationIcons.club },
    { id: 'evento', label: 'Evento', icon: pollLocationIcons.evento },
  ];

  if (pageState === 'loading') {
    return (
      <div className="min-h-screen bg-background-50">
        <section className="relative w-full h-[280px] md:h-[380px] bg-background-200 animate-pulse" />
        <main className="max-w-[1280px] mx-auto px-4 md:px-6 py-8 space-y-8">
          <div className="flex gap-2 flex-wrap">{[1,2,3,4,5,6].map((i) => (<div key={i} className="bg-background-100 rounded-full h-8 w-20 animate-pulse" />))}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">{[1,2,3,4,5,6].map((i) => (<div key={i} className="bg-background-100 rounded-2xl h-[300px] animate-pulse" />))}</div>
        </main>
      </div>
    );
  }

  if (pageState === 'error') {
    return (
      <div className="min-h-screen bg-background-50 flex flex-col items-center justify-center px-4 py-20">
        <div className="w-20 h-20 rounded-3xl bg-red-100 flex items-center justify-center mb-5">
          <i className="ri-error-warning-line text-3xl text-red-500"></i>
        </div>
        <h2 className="text-xl font-extrabold text-foreground-950 mb-2">Error al cargar encuestas</h2>
        <p className="text-sm text-foreground-500 mb-6 text-center max-w-md">No pudimos cargar las encuestas. Verificá tu conexión e intentá de nuevo.</p>
        <button onClick={handleRetry} className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-xl text-sm font-bold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
          <i className="ri-refresh-line"></i>Reintentar
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      {/* HERO */}
      <section className="relative w-full h-[300px] md:h-[420px] overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Interactive%20digital%20poll%20and%20survey%20interface%20displayed%20on%20large%20screens%20in%20modern%20tech%20environment%2C%20colorful%20data%20visualization%20bars%20and%20charts%2C%20diverse%20group%20of%20people%20engaging%20with%20voting%20tablets%2C%20dynamic%20community%20participation%20scene%2C%20warm%20ambient%20lighting%2C%20modern%20collaborative%20atmosphere&width=1600&height=900&seq=rfw-encuestas-hero&orientation=landscape"
          alt="Encuestas RFW"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/60"></div>
        <div className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center">
          <div className="w-16 h-16 rounded-2xl bg-background-50/15 backdrop-blur-md flex items-center justify-center mb-4 border border-background-50/20">
            <i className="ri-chat-poll-line text-3xl text-background-50"></i>
          </div>
          <h1 className="text-3xl md:text-5xl font-extrabold text-background-50 leading-tight mb-3">
            Sistema de Encuestas
          </h1>
          <p className="text-sm md:text-base text-background-50/80 max-w-xl leading-relaxed">
            Tu voz define el contenido. Encuestas en Cabina, Radio, Programas, Locutores, Clubes y Eventos. Resultados en tiempo real.
          </p>
          <div className="flex items-center gap-3 mt-5">
            <span className="text-background-50/60 text-xs">{rfwPolls.length} encuestas · {pollDashboardStats.activeNow} activas · {formatCount(pollDashboardStats.totalVotes)} votos</span>
          </div>
        </div>
      </section>

      {/* MAIN */}
      <main className="max-w-[1280px] mx-auto px-4 md:px-6 py-8 md:py-12 space-y-8">
        {/* STATS STRIP */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { label: 'Encuestas Totales', value: pollDashboardStats.totalPolls.toString(), icon: 'ri-chat-poll-line', color: 'text-secondary-600 bg-secondary-50' },
            { label: 'Activas Ahora', value: pollDashboardStats.activeNow.toString(), icon: 'ri-flashlight-line', color: 'text-emerald-600 bg-emerald-50' },
            { label: 'Votos Totales', value: formatCount(pollDashboardStats.totalVotes), icon: 'ri-bar-chart-line', color: 'text-amber-600 bg-amber-50' },
            { label: 'Participantes', value: formatCount(pollDashboardStats.totalParticipants), icon: 'ri-group-line', color: 'text-sky-600 bg-sky-50' },
            { label: 'En Cabina', value: pollDashboardStats.activeInCabina.toString(), icon: 'ri-chat-smile-2-line', color: 'text-red-600 bg-red-50' },
          ].map((stat, i) => (
            <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-background-100 border border-background-200/70">
              <div className={`w-9 h-9 rounded-full ${stat.color} flex items-center justify-center shrink-0`}>
                <i className={`${stat.icon} text-sm`}></i>
              </div>
              <div>
                <p className="text-lg font-bold text-foreground-950">{stat.value}</p>
                <p className="text-[10px] text-foreground-500">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* TYPE FILTERS */}
        <div className="flex flex-wrap items-center gap-1.5">
          {typeTabs.map((tab) => {
            const isActive = typeFilter === tab.id;
            const typeColor = tab.id !== 'todos' ? pollTypeTextColors[tab.id as PollType] : 'text-foreground-600';
            const typeBg = tab.id !== 'todos' ? pollTypeBgLight[tab.id as PollType] : 'bg-background-100';
            return (
              <button
                key={tab.id}
                onClick={() => setTypeFilter(tab.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 whitespace-nowrap border cursor-pointer ${
                  isActive ? `border-current shadow-sm ${typeColor} ${typeBg}` : 'border-background-200 bg-background-50 text-foreground-500 hover:text-foreground-700 hover:border-background-300'
                }`}
              >
                <i className={`${tab.icon} text-[10px]`}></i>
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* LOCATION + STATUS FILTERS */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex flex-wrap items-center gap-1.5">
            {locationTabs.map((tab) => {
              const isActive = locationFilter === tab.id;
              const locColor = tab.id !== 'todos' ? pollLocationColors[tab.id as PollLocation] : '';
              return (
                <button
                  key={tab.id}
                  onClick={() => setLocationFilter(tab.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer border ${
                    isActive ? locColor : 'border-background-200 bg-background-50 text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  <i className={`${tab.icon} text-[10px]`}></i>
                  {tab.label}
                </button>
              );
            })}
          </div>

          <div className="flex items-center gap-1.5">
            {(['todos', 'active', 'closed', 'scheduled'] as const).map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer ${
                  statusFilter === s ? 'bg-foreground-950 text-background-50' : 'bg-background-100 text-foreground-500 hover:text-foreground-700'
                }`}
              >
                {s === 'todos' ? 'Todas' : s === 'active' ? 'Activas' : s === 'closed' ? 'Cerradas' : 'Programadas'}
              </button>
            ))}
          </div>
        </div>

        {/* CABINA HIGHLIGHT */}
        {cabinaActive.length > 0 && (
          <div className="p-5 rounded-2xl bg-red-50 border border-red-200">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
                <i className="ri-chat-smile-2-line text-sm text-red-600"></i>
              </div>
              <div>
                <p className="text-sm font-bold text-foreground-950">Encuestas en Cabina</p>
                <p className="text-[10px] text-foreground-500">{cabinaActive.length} activas ahora · Aparecen inmediatamente para los oyentes</p>
              </div>
            </div>
          </div>
        )}

        {/* RESULTS COUNT */}
        <p className="text-[11px] text-foreground-400">
          {filtered.length === 0 ? 'No se encontraron encuestas con esos filtros.' : `Mostrando ${filtered.length} encuesta${filtered.length !== 1 ? 's' : ''}`}
        </p>

        {/* POLLS GRID */}
        {filtered.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((poll) => {
              const isActive = poll.status === 'active';
              const isClosed = poll.status === 'closed';
              const isScheduled = poll.status === 'scheduled';
              const timeText = getTimeRemainingText(poll.closesAt);
              const isClosingSoon = isActive && (timeText.includes('min') || timeText.includes('h'));
              const totalVotes = poll.options.reduce((sum, o) => sum + o.votes, 0);
              const maxVotes = Math.max(...poll.options.map((o) => o.votes), 1);

              return (
                <div
                  key={poll.id}
                  className="rounded-2xl bg-background-50 border border-background-200/70 overflow-hidden hover:border-secondary-300/60 transition-all duration-300 group cursor-pointer"
                  onClick={() => { setSelectedPoll(poll); setDetailTab(isClosed ? 'resultados' : 'votar'); }}
                >
                  <div className="p-4 pb-2">
                    <div className="flex items-center gap-1.5 mb-2">
                      {isActive && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 text-[9px] font-bold">
                          <span className={`w-1.5 h-1.5 rounded-full ${isClosingSoon ? 'bg-amber-500 animate-pulse' : 'bg-emerald-500'}`}></span>
                          {isClosingSoon ? 'Cierra pronto' : 'Activa'}
                        </span>
                      )}
                      {isClosed && <span className="px-2 py-0.5 rounded-full bg-foreground-100 text-foreground-500 text-[9px] font-medium">Cerrada</span>}
                      {isScheduled && <span className="px-2 py-0.5 rounded-full bg-sky-50 text-sky-600 text-[9px] font-medium">Programada</span>}
                      <span className={`px-2 py-0.5 rounded-full text-[9px] font-medium border ${pollTypeColors[poll.type]}`}>
                        {pollTypeLabels[poll.type]}
                      </span>
                      {poll.location === 'cabina' && (
                        <span className="px-1.5 py-0.5 rounded-full bg-red-50 text-red-600 text-[9px] font-bold">Cabina</span>
                      )}
                    </div>
                    <p className="text-sm font-bold text-foreground-900 leading-snug mb-2 line-clamp-2">{poll.question}</p>
                    <div className="flex items-center gap-2 text-[10px] mb-3">
                      <span className="flex items-center gap-1 text-foreground-500">
                        <i className={`${pollLocationIcons[poll.location]} text-[9px]`}></i>
                        {poll.entityName}
                      </span>
                      {isActive && (
                        <span className="flex items-center gap-1 text-amber-600 font-medium">
                          <i className="ri-timer-line text-[9px]"></i>
                          {timeText}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="px-4 pb-4 space-y-2">
                    {poll.options.slice(0, 4).map((opt) => {
                      const pct = totalVotes > 0 ? Math.round((opt.votes / totalVotes) * 100) : 0;
                      const barWidth = totalVotes > 0 ? Math.max((opt.votes / maxVotes) * 100, 8) : 8;
                      const isWinner = poll.winnerId === opt.id;
                      return (
                        <div key={opt.id}>
                          <div className="flex items-center justify-between mb-0.5">
                            <span className="text-[11px] font-medium text-foreground-800 truncate pr-2">
                              {isWinner && <span className="text-amber-500 mr-0.5">🏆</span>}
                              {opt.text}
                            </span>
                            <span className="text-[10px] font-bold text-foreground-600 shrink-0">{pct}%</span>
                          </div>
                          <div className="h-1.5 rounded-full bg-background-200 overflow-hidden">
                            <div className={`h-full rounded-full transition-all duration-500 ${isWinner ? 'bg-amber-500' : 'bg-secondary-500'}`} style={{ width: `${barWidth}%` }} />
                          </div>
                        </div>
                      );
                    })}
                    {poll.options.length > 4 && (
                      <p className="text-[10px] text-foreground-400 pt-1">+{poll.options.length - 4} opciones más</p>
                    )}
                  </div>

                  <div className="px-4 pb-3 pt-2 border-t border-background-200/70 flex items-center justify-between text-[10px] text-foreground-500">
                    <div className="flex items-center gap-3">
                      <span className="flex items-center gap-1"><i className="ri-bar-chart-line text-[9px]"></i>{formatCount(totalVotes)}</span>
                      <span className="flex items-center gap-1"><i className="ri-group-line text-[9px]"></i>{formatCount(poll.participants)}</span>
                      <span className="flex items-center gap-1"><i className="ri-chat-3-line text-[9px]"></i>{formatCount(poll.comments)}</span>
                    </div>
                    <span className="flex items-center gap-1 text-secondary-600 font-medium">
                      <i className="ri-flashlight-line text-[9px]"></i>+{poll.score.vote} pts
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-16 h-16 rounded-2xl bg-background-100 flex items-center justify-center mb-4">
              <i className="ri-chat-poll-line text-2xl text-foreground-300"></i>
            </div>
            <p className="text-sm font-semibold text-foreground-400">No se encontraron encuestas</p>
            <p className="text-[12px] text-foreground-300 mt-1">Intentá ajustar los filtros</p>
          </div>
        )}

        {/* ¿CÓMO FUNCIONA? */}
        <section className="bg-background-100 rounded-3xl p-6 md:p-8">
          <div className="text-center mb-6">
            <div className="w-12 h-12 rounded-full bg-secondary-100 flex items-center justify-center mx-auto mb-3">
              <i className="ri-lightbulb-line text-xl text-secondary-600"></i>
            </div>
            <h3 className="text-lg font-extrabold text-foreground-950 mb-2">¿Cómo Funcionan las Encuestas?</h3>
            <p className="text-xs text-foreground-500 max-w-xl mx-auto">5 tipos de encuesta distribuidas en 6 ubicaciones. Cada interacción suma Score.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
            {typeTabs.filter((t) => t.id !== 'todos').map((tab) => (
              <div key={tab.id} className="text-center p-3 rounded-xl bg-background-50 border border-background-200/70">
                <div className="w-10 h-10 rounded-full bg-background-200 flex items-center justify-center mx-auto mb-2">
                  <i className={`${tab.icon} text-sm text-foreground-600`}></i>
                </div>
                <p className="text-[11px] font-semibold text-foreground-800">{tab.label}</p>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {locationTabs.filter((l) => l.id !== 'todos').map((loc) => (
              <div key={loc.id} className="text-center p-3 rounded-xl bg-background-50 border border-background-200/70">
                <div className="w-10 h-10 rounded-full bg-background-200 flex items-center justify-center mx-auto mb-2">
                  <i className={`${loc.icon} text-sm text-foreground-600`}></i>
                </div>
                <p className="text-[11px] font-semibold text-foreground-800">{loc.label}</p>
              </div>
            ))}
          </div>
          <div className="mt-6 text-center">
            <p className="text-xs text-foreground-500">
              Crear una encuesta da <strong className="text-foreground-800">+50 a +350 pts</strong>. Votar suma <strong className="text-foreground-800">+10 pts</strong>. Compartir suma <strong className="text-foreground-800">+15 pts</strong>. Comentar suma <strong className="text-foreground-800">+5 pts</strong>.
            </p>
          </div>
        </section>

        {/* CROSS LINKS */}
        <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link to="/rankings" className="p-4 rounded-xl bg-background-100 border border-background-200/70 hover:border-accent-200/60 transition-all duration-200 text-center">
            <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center mx-auto mb-2">
              <i className="ri-trophy-line text-base text-accent-600"></i>
            </div>
            <p className="text-sm font-semibold text-foreground-900">Rankings RFW</p>
            <p className="text-[10px] text-foreground-500 mt-1">Conocé quiénes lideran en cada categoría</p>
          </Link>
          <Link to="/clubes" className="p-4 rounded-xl bg-background-100 border border-background-200/70 hover:border-violet-300/60 transition-all duration-200 text-center">
            <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center mx-auto mb-2">
              <i className="ri-shield-star-line text-base text-violet-600"></i>
            </div>
            <p className="text-sm font-semibold text-foreground-900">Clubes RFW</p>
            <p className="text-[10px] text-foreground-500 mt-1">Unite a un club y participá en encuestas exclusivas</p>
          </Link>
          <Link to="/eventos" className="p-4 rounded-xl bg-background-100 border border-background-200/70 hover:border-orange-300/60 transition-all duration-200 text-center">
            <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center mx-auto mb-2">
              <i className="ri-calendar-event-line text-base text-orange-600"></i>
            </div>
            <p className="text-sm font-semibold text-foreground-900">Eventos RFW</p>
            <p className="text-[10px] text-foreground-500 mt-1">Descubrí eventos con encuestas en vivo</p>
          </Link>
        </section>

        {/* REGLA FINAL */}
        <section className="p-6 rounded-2xl bg-secondary-100/70 border border-secondary-200/50 text-center">
          <div className="w-12 h-12 rounded-full bg-secondary-500 flex items-center justify-center mx-auto mb-3">
            <i className="ri-chat-poll-line text-xl text-white"></i>
          </div>
          <p className="text-lg font-extrabold text-foreground-950 mb-2">Si la Cabina tiene encuestas activadas, deben aparecer inmediatamente para los oyentes.</p>
          <p className="text-sm text-foreground-600 max-w-xl mx-auto">
            Las encuestas generan interacción constante. No son solo preguntas — son el pulso de tu comunidad en tiempo real. Cada voto es un oyente participando. Cada resultado es contenido.
          </p>
        </section>
      </main>

      {/* ─── POLL DETAIL MODAL ─── */}
      {selectedPoll && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setSelectedPoll(null)}></div>
          <div className="relative bg-background-50 rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
            <button
              onClick={() => setSelectedPoll(null)}
              className="absolute top-4 right-4 z-20 w-8 h-8 rounded-full bg-background-50/80 backdrop-blur-md flex items-center justify-center hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-close-line text-foreground-700"></i>
            </button>

            <div className="p-5 md:p-6 pt-5 md:pt-6">
              {/* Header */}
              <div className="flex items-center gap-2 mb-3">
                {selectedPoll.status === 'active' && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 text-[10px] font-bold">
                    <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                    {getTimeRemainingText(selectedPoll.closesAt).includes('min') || getTimeRemainingText(selectedPoll.closesAt).includes('h') ? 'Cierra pronto' : 'Activa'}
                  </span>
                )}
                {selectedPoll.status === 'closed' && <span className="px-2 py-0.5 rounded-full bg-foreground-100 text-foreground-500 text-[10px] font-medium">Cerrada</span>}
                {selectedPoll.status === 'scheduled' && <span className="px-2 py-0.5 rounded-full bg-sky-50 text-sky-600 text-[10px] font-medium">Programada</span>}
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${pollTypeColors[selectedPoll.type]}`}>
                  {pollTypeLabels[selectedPoll.type]}
                </span>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${pollLocationColors[selectedPoll.location]}`}>
                  {selectedPoll.locationName}
                </span>
              </div>

              <h2 className="text-lg md:text-xl font-extrabold text-foreground-950 leading-tight mb-2">{selectedPoll.question}</h2>
              {selectedPoll.description && (
                <p className="text-xs text-foreground-600 leading-relaxed mb-2">{selectedPoll.description}</p>
              )}

              <div className="flex items-center gap-3 text-[11px] text-foreground-500 mb-4">
                <span className="flex items-center gap-1">
                  <img src={selectedPoll.entityLogoUrl} alt="" className="w-5 h-5 rounded-full object-cover" />
                  {selectedPoll.entityName}
                </span>
                <span className="flex items-center gap-1"><i className="ri-calendar-line text-[10px]"></i>{formatPollDate(selectedPoll.createdAt)}</span>
                {selectedPoll.status === 'active' && (
                  <span className="flex items-center gap-1 text-amber-600 font-medium">
                    <i className="ri-timer-line text-[10px]"></i>Cierra: {formatPollDateShort(selectedPoll.closesAt)}
                  </span>
                )}
              </div>

              {/* Tabs */}
              <div className="flex items-center border-b border-background-200/70 mb-4">
                {[
                  { id: 'votar' as const, label: 'Votar', icon: 'ri-checkbox-circle-line', show: selectedPoll.status === 'active' || selectedPoll.status === 'scheduled' },
                  { id: 'resultados' as const, label: 'Resultados', icon: 'ri-bar-chart-line', show: true },
                  { id: 'score' as const, label: 'Score', icon: 'ri-flashlight-line', show: true },
                ].filter((t) => t.show).map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setDetailTab(tab.id)}
                    className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-semibold border-b-2 transition-all whitespace-nowrap cursor-pointer ${
                      detailTab === tab.id ? 'border-secondary-500 text-secondary-600' : 'border-transparent text-foreground-400 hover:text-foreground-600'
                    }`}
                  >
                    <i className={`${tab.icon} text-[10px]`}></i>
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Tab: Votar */}
              {detailTab === 'votar' && (
                <div className="space-y-2">
                  {selectedPoll.status === 'scheduled' ? (
                    <div className="text-center py-8">
                      <div className="w-12 h-12 rounded-full bg-sky-100 flex items-center justify-center mx-auto mb-3">
                        <i className="ri-timer-line text-lg text-sky-500"></i>
                      </div>
                      <p className="text-sm font-semibold text-foreground-700">Esta encuesta aún no está activa</p>
                      <p className="text-xs text-foreground-500 mt-1">Estará disponible a partir del {formatPollDate(selectedPoll.createdAt)}</p>
                    </div>
                  ) : (
                    <>
                      <p className="text-[10px] text-foreground-500 mb-2">
                        {selectedPoll.type === 'single' || selectedPoll.type === 'quick'
                          ? 'Seleccioná una opción'
                          : selectedPoll.type === 'multiple'
                          ? 'Seleccioná todas las que quieras'
                          : 'Seleccioná tu opción favorita'}
                      </p>
                      {selectedPoll.options.map((opt) => {
                        const votedOptions = getVotedOptions(selectedPoll.id);
                        const isSelected = votedOptions.includes(opt.id);
                        const totalVotes = selectedPoll.options.reduce((s, o) => s + o.votes, 0);
                        const pct = totalVotes > 0 ? Math.round((opt.votes / totalVotes) * 100) : 0;
                        const maxVotes = Math.max(...selectedPoll.options.map((o) => o.votes), 1);
                        const barWidth = Math.max((opt.votes / maxVotes) * 100, 5);

                        return (
                          <button
                            key={opt.id}
                            onClick={() => handleVote(selectedPoll.id, opt.id, selectedPoll.type)}
                            className={`w-full text-left p-3 rounded-xl border transition-all duration-200 cursor-pointer ${
                              isSelected ? 'bg-secondary-100 border-secondary-400' : 'bg-background-50 border-background-200/70 hover:border-secondary-200/60'
                            }`}
                          >
                            <div className="flex items-center justify-between mb-1.5">
                              <span className="text-sm font-medium text-foreground-800">{opt.text}</span>
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-bold text-foreground-600">{pct}%</span>
                                {isSelected && (
                                  <span className="w-5 h-5 rounded-full bg-secondary-500 flex items-center justify-center">
                                    <i className="ri-check-line text-[10px] text-white"></i>
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="h-2 rounded-full bg-background-200 overflow-hidden">
                              <div className={`h-full rounded-full transition-all duration-500 ${isSelected ? 'bg-secondary-500' : 'bg-background-300'}`} style={{ width: `${barWidth}%` }} />
                            </div>
                          </button>
                        );
                      })}
                    </>
                  )}
                </div>
              )}

              {/* Tab: Resultados */}
              {detailTab === 'resultados' && (
                <div className="space-y-2">
                  {selectedPoll.winnerId && (
                    <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 mb-3 text-center">
                      <p className="text-[10px] text-amber-700 font-semibold mb-0.5">Ganador</p>
                      <p className="text-sm font-extrabold text-foreground-900">🏆 {getWinnerText(selectedPoll)}</p>
                    </div>
                  )}
                  {selectedPoll.options.map((opt) => {
                    const totalVotes = selectedPoll.options.reduce((s, o) => s + o.votes, 0);
                    const pct = totalVotes > 0 ? Math.round((opt.votes / totalVotes) * 100) : 0;
                    const maxVotes = Math.max(...selectedPoll.options.map((o) => o.votes), 1);
                    const barWidth = Math.max((opt.votes / maxVotes) * 100, 5);
                    const isWinner = selectedPoll.winnerId === opt.id;
                    return (
                      <div key={opt.id} className="p-3 rounded-xl bg-background-50 border border-background-200/70">
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-sm font-medium text-foreground-800">
                            {isWinner && <span className="text-amber-500 mr-1">🏆</span>}
                            {opt.text}
                          </span>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-foreground-500">{formatCount(opt.votes)} votos</span>
                            <span className="text-xs font-bold text-foreground-700">{pct}%</span>
                          </div>
                        </div>
                        <div className="h-2.5 rounded-full bg-background-200 overflow-hidden">
                          <div className={`h-full rounded-full transition-all duration-700 ${isWinner ? 'bg-amber-500' : 'bg-secondary-500'}`} style={{ width: `${barWidth}%` }} />
                        </div>
                      </div>
                    );
                  })}
                  <div className="grid grid-cols-3 gap-2 mt-4 pt-3 border-t border-background-200/70">
                    <div className="text-center p-2 rounded-lg bg-background-100">
                      <p className="text-lg font-bold text-foreground-950">{formatCount(selectedPoll.totalVotes)}</p>
                      <p className="text-[9px] text-foreground-400">Votos</p>
                    </div>
                    <div className="text-center p-2 rounded-lg bg-background-100">
                      <p className="text-lg font-bold text-foreground-950">{formatCount(selectedPoll.participants)}</p>
                      <p className="text-[9px] text-foreground-400">Participantes</p>
                    </div>
                    <div className="text-center p-2 rounded-lg bg-background-100">
                      <p className="text-lg font-bold text-foreground-950">{formatCount(selectedPoll.comments)}</p>
                      <p className="text-[9px] text-foreground-400">Comentarios</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Tab: Score */}
              {detailTab === 'score' && (
                <div className="space-y-3">
                  <p className="text-xs text-foreground-500">Cada acción relacionada con esta encuesta genera puntos RFW:</p>
                  <div className="flex items-center justify-between p-3 rounded-xl bg-background-100">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                        <i className="ri-add-circle-line text-sm text-emerald-600"></i>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground-900">Crear Encuesta</p>
                        <p className="text-[10px] text-foreground-500">La entidad que crea la encuesta</p>
                      </div>
                    </div>
                    <span className="text-lg font-extrabold text-emerald-600 shrink-0">+{selectedPoll.score.createPoll}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-xl bg-background-100">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                        <i className="ri-checkbox-circle-line text-sm text-amber-600"></i>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground-900">Votar</p>
                        <p className="text-[10px] text-foreground-500">Cada voto en la encuesta</p>
                      </div>
                    </div>
                    <span className="text-lg font-extrabold text-amber-600 shrink-0">+{selectedPoll.score.vote}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-xl bg-background-100">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center">
                        <i className="ri-share-forward-line text-sm text-violet-600"></i>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground-900">Compartir</p>
                        <p className="text-[10px] text-foreground-500">Difundir la encuesta</p>
                      </div>
                    </div>
                    <span className="text-lg font-extrabold text-violet-600 shrink-0">+{selectedPoll.score.share}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 rounded-xl bg-background-100">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-rose-100 flex items-center justify-center">
                        <i className="ri-chat-3-line text-sm text-rose-600"></i>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground-900">Comentar</p>
                        <p className="text-[10px] text-foreground-500">Dejar un comentario</p>
                      </div>
                    </div>
                    <span className="text-lg font-extrabold text-rose-600 shrink-0">+{selectedPoll.score.comment}</span>
                  </div>
                  <div className="p-4 rounded-xl bg-secondary-50 border border-secondary-200 text-center mt-4">
                    <p className="text-xs font-semibold text-foreground-800">Score Total Potencial</p>
                    <p className="text-2xl font-extrabold text-secondary-600">
                      +{selectedPoll.score.createPoll + selectedPoll.score.vote + selectedPoll.score.share + selectedPoll.score.comment} pts
                    </p>
                    <p className="text-[10px] text-foreground-500 mt-1">Sumando todas las acciones posibles</p>
                  </div>
                </div>
              )}

              {/* Actions Bar */}
              <div className="flex flex-wrap gap-2 pt-4 mt-4 border-t border-background-200/70">
                <button className="flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold bg-violet-50 text-violet-700 hover:bg-violet-100 transition-all duration-200 whitespace-nowrap cursor-pointer">
                  <i className="ri-share-forward-line text-[10px]"></i>Compartir
                </button>
                <div className="flex-1"></div>
                <button
                  onClick={() => setSelectedPoll(null)}
                  className="px-4 py-2 rounded-full text-xs font-medium text-foreground-500 hover:text-foreground-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}